import { Field, cx, inputBase, inputClass } from "@/platform/ui";
import {
  WEEKDAYS,
  defaultUntil,
  type CustomUnit,
  type EndMode,
  type RecurrenceForm,
  type RepeatMode,
} from "@/planning/ui/lib/recurrenceForm.js";
import type { Weekday } from "@/platform/kit";

// EventModal's "Repeat" field: mode (none/daily/weekly/monthly/yearly/custom),
// the custom interval+unit+weekday picker, and the series end condition.
// Extracted as a self-contained field since EventModal only ever renders it
// wrapped in its own visibility check (an override never has its own rule).
export function RecurrenceEditor({
  recur,
  sDate,
  onChange,
}: {
  recur: RecurrenceForm;
  sDate: string;
  onChange: (p: Partial<RecurrenceForm>) => void;
}) {
  const toggleDay = (d: Weekday) =>
    onChange({
      byDay: recur.byDay.includes(d) ? recur.byDay.filter((x) => x !== d) : [...recur.byDay, d],
    });
  // Turning repeat on for the first time defaults the series to end "on this
  // day" (a year out) rather than never — an explicit, visible end beats a
  // silently-open-ended series.
  function setRepeatMode(mode: RepeatMode) {
    if (recur.mode === "none" && mode !== "none") {
      onChange({ mode, endMode: "on", until: recur.until || defaultUntil(sDate) });
    } else {
      onChange({ mode });
    }
  }

  return (
    <Field label="Repeat">
      <div className="space-y-2">
        <select
          className={inputClass}
          value={recur.mode}
          onChange={(e) => setRepeatMode(e.target.value as RepeatMode)}
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="custom">Custom…</option>
        </select>

        {recur.mode === "custom" && (
          <div className="space-y-2.5 rounded-lg border border-line bg-surface-2 p-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted">Every</span>
              <input
                type="number"
                min={1}
                className={cx("mono", inputBase, "w-16")}
                value={recur.interval}
                onChange={(e) => onChange({ interval: Number(e.target.value) })}
              />
              <select
                className={cx(inputBase, "w-32")}
                value={recur.unit}
                onChange={(e) => onChange({ unit: e.target.value as CustomUnit })}
              >
                {(["day", "week", "month", "year"] as CustomUnit[]).map((u) => (
                  <option key={u} value={u}>
                    {u}
                    {recur.interval === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </div>

            {recur.unit === "week" && (
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDay(d.value)}
                    className={cx(
                      "rounded-full border px-2 py-0.5 text-[11px] font-bold",
                      recur.byDay.includes(d.value)
                        ? "border-accent bg-accent-soft text-accent"
                        : "border-line text-dim hover:bg-surface",
                    )}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Shown for every repeat mode, not just Custom — a series always
            has a real end condition to pick, defaulted to "On this day"
            the moment repeat is turned on (see setRepeatMode). */}
        {recur.mode !== "none" && (
          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface-2 p-2.5">
            <span className="text-xs font-semibold text-muted">Ends</span>
            <select
              className={cx(inputBase, "w-32")}
              value={recur.endMode}
              onChange={(e) => onChange({ endMode: e.target.value as EndMode })}
            >
              <option value="on">On this day</option>
              <option value="after">After</option>
              <option value="never">No end date</option>
            </select>
            {recur.endMode === "on" && (
              <input
                type="date"
                className={cx("mono", inputBase, "min-w-0 flex-1")}
                value={recur.until}
                onChange={(e) => onChange({ until: e.target.value })}
              />
            )}
            {recur.endMode === "after" && (
              <span className="flex items-center gap-1.5">
                <input
                  type="number"
                  min={1}
                  className={cx("mono", inputBase, "w-16")}
                  value={recur.count}
                  onChange={(e) => onChange({ count: Number(e.target.value) })}
                />
                <span className="text-xs text-muted">occurrences</span>
              </span>
            )}
          </div>
        )}
      </div>
    </Field>
  );
}
